import { queryClient } from "@/api/queryClient";
import { useAuthStore } from "@/store/auth";
import { DEVICE_ID_KEY } from "@/utils/deviceId";

/**
 * Nuclear reset escape hatch: wipe all persisted client state and return to the
 * login page. Used by the always-available "Wyczyść i wyloguj" button so a user
 * whose session is wedged (e.g. a stale token that leaves ProtectedLayout stuck
 * on the loading spinner) can always recover without touching DevTools.
 *
 * Preserves only the device id so the reset isn't treated as a brand-new device
 * and doesn't force an SMS 2FA on the next login. Everything else goes.
 */
// Klucze localStorage, które przy ZWYKŁYM wylogowaniu nie mogą zostać dla
// następnej osoby przy tym samym komputerze (audyt cache 2026-09-29):
// nienapisane wiadomości/komentarze (treść), snapshot liczników nav-badges
// (cudze liczby w sidebarze do 90 s) i pogoda (przybliżona lokalizacja).
// Preferencje UI i filtry per-user zostają — to wygoda, nie wyciek.
const PRIVATE_KEY_PREFIXES = ["comment-draft-", "chat_draft_", "gg_draft_", "gg_reply_"];
const PRIVATE_KEYS = [
  "elg5.weather.v1",
  "elg5-nav-badges-snapshot",
  "elg5-nav-badges-requested-at",
  "elg5-notif-leader-ts",
];

/** Remove drafts and other user-private localStorage entries on deliberate logout. */
export function clearPrivateLocalState(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (PRIVATE_KEYS.includes(key) || PRIVATE_KEY_PREFIXES.some((p) => key.startsWith(p))) {
        doomed.push(key);
      }
    }
    doomed.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage may be unavailable in some privacy modes — ignore.
  }
}

export function clearSession(): void {
  // Keep the device identity across the wipe.
  const deviceId = localStorage.getItem(DEVICE_ID_KEY);

  useAuthStore.getState().logout({ deliberate: true });
  queryClient.clear();

  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch {
    // Storage may be unavailable in some privacy modes — ignore and press on.
  }

  if (deviceId) {
    try {
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    } catch {
      // ignore
    }
  }

  // Tear down the realtime socket so it doesn't reconnect with a dead token.
  // Dynamic import keeps this util free of a static chat dependency.
  import("@/features/chat/api/realtime")
    .then((m) => m.disconnectRealtime())
    .catch(() => {});

  // Full navigation (not SPA) guarantees every in-memory store resets too.
  window.location.href = "/auth/login";
}
